import React from 'react';
import { Form, useActionData, useNavigate, useNavigation, useRevalidator } from 'react-router-dom';

import { Button } from '@oysterjs/ui/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { AutoResizeTextarea, Textarea } from '@oysterjs/ui/textarea';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';

import {
  Action,
  Resource,
  type ExcelSpreadsheetTemplateFieldsFragment,
  type UpdateExcelSpreadsheetTemplateMutation
} from '~/api/graphql';
import { FileInput } from '~/components/file';
import { PermissionNotice, usePermissions } from '~/components/permissions';
import { VisibilitySplitButton } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import {
  visibilityInputFromValue,
  visibilityValueFromFragment,
  type VisibilitySelectorValue
} from '~/components/visibility/visibility-selector.utils';

import { ExcelTemplateTabFields } from './excel-template-tab-fields';
import { EXCEL_EXTENSION_ERROR, EXCEL_EXTENSION_RE } from './excel-template-create-dialog';
import {
  appendExcelTabsToFormData,
  formStateFromExcelTemplate,
  type ExcelTemplateFormState
} from '~/components/library/lib/excel-template-tabs';
import { OfficeOnlineFilePreview } from '~/components/office-online-embed-url';

const UPDATE_EXCEL_TEMPLATE = [
  { resource: Resource.ExcelSpreadsheetTemplate, action: Action.Update }
];

const POST_SAVE_CLOSE_DELAY_MS = 1000;

/**
 * Edit slideout for Excel spreadsheet templates: workbook-level metadata and
 * per-tab guidance on the left, a live preview of the stored workbook on the
 * right.
 *
 * Submission is delegated to `props.onSubmit` so the parent route decides how
 * to navigate after the update. The component builds the `multipart/form-data`
 * payload (metadata, optional replacement workbook, and the tabs JSON) that the
 * `updateExcelSpreadsheetTemplate` route action knows how to parse.
 */
export function ExcelTemplateEditor(props: {
  excelTemplate: ExcelSpreadsheetTemplateFieldsFragment;
  validationErrors: ValidationError[];
  /** URL of the list page to navigate back to on close. */
  listUrl: string;
  onSubmit: (formData: FormData) => void;
}) {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const action = useActionData() as ActionResult<UpdateExcelSpreadsheetTemplateMutation>;

  const visibilityEditAllowed = useVisibilityEditAllowed(props.excelTemplate.createdBy?.id);
  const [visibility, setVisibility] = React.useState<VisibilitySelectorValue>(
    visibilityValueFromFragment(props.excelTemplate.visibility)
  );

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>(
    props.validationErrors
  );
  const [form, setForm] = React.useState<ExcelTemplateFormState>(() =>
    formStateFromExcelTemplate(
      props.excelTemplate,
      visibilityInputFromValue(visibilityValueFromFragment(props.excelTemplate.visibility))
    )
  );
  const [replacementWorkbook, setReplacementWorkbook] = React.useState<File | null>(null);
  const { updateForm, getValidationError } = useFormActions<ExcelTemplateFormState>(
    setForm,
    setValidationErrors
  );
  const [error, setError] = React.useState<string>();
  const [closingAfterSave, setClosingAfterSave] = React.useState(false);
  const handledSaveKeyRef = React.useRef<string | null>(null);

  // Re-seed local state when the route swaps to a different template. Edits on
  // the same template are intentionally preserved by checking that the id changed.
  const lastTemplateIdRef = React.useRef(props.excelTemplate.id);
  React.useEffect(() => {
    if (lastTemplateIdRef.current === props.excelTemplate.id) return;
    lastTemplateIdRef.current = props.excelTemplate.id;
    const nextVisibility = visibilityValueFromFragment(props.excelTemplate.visibility);
    setVisibility(nextVisibility);
    setForm(
      formStateFromExcelTemplate(props.excelTemplate, visibilityInputFromValue(nextVisibility))
    );
    setReplacementWorkbook(null);
    setValidationErrors([]);
    setError(undefined);
  }, [props.excelTemplate]);

  React.useEffect(() => {
    if (props.validationErrors?.length) {
      setValidationErrors(props.validationErrors);
    }
  }, [props.validationErrors]);

  // After a successful update, hold the loading state briefly so ingestion can
  // transition on the server, revalidate the library list, then close.
  React.useEffect(() => {
    const updated = action?.data?.updateExcelSpreadsheetTemplate;
    if (!updated?.id || action?.validationErrors?.length) {
      return;
    }
    const saveKey = `${updated.id}:${updated.updatedAt}`;
    if (handledSaveKeyRef.current === saveKey) {
      return;
    }
    handledSaveKeyRef.current = saveKey;
    setClosingAfterSave(true);
    const timer = window.setTimeout(() => {
      revalidator.revalidate();
      navigate(props.listUrl);
    }, POST_SAVE_CLOSE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    action?.data?.updateExcelSpreadsheetTemplate,
    action?.validationErrors,
    navigate,
    props.listUrl,
    revalidator
  ]);

  React.useEffect(() => {
    handledSaveKeyRef.current = null;
    setClosingAfterSave(false);
  }, [props.excelTemplate.id]);

  const onClose = () => {
    navigate(props.listUrl);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (replacementWorkbook && !EXCEL_EXTENSION_RE.test(replacementWorkbook.name)) {
      setError(EXCEL_EXTENSION_ERROR);
      return;
    }
    setError(undefined);

    const formData = new FormData();
    formData.set('title', form.title);
    formData.set('description', form.description);
    formData.set('fillGuidance', form.fillGuidance ?? '');
    if (replacementWorkbook) {
      formData.set('uploadTemplate', replacementWorkbook);
    } else if (form.templateFileId) {
      // Pin the stored workbook so an edit that only touches metadata keeps it.
      formData.set('templateFileId', form.templateFileId);
    }
    if (visibilityEditAllowed) {
      formData.set('visibility', JSON.stringify(visibilityInputFromValue(visibility)));
    }
    appendExcelTabsToFormData(formData, form.tabs);

    props.onSubmit(formData);
  };

  const canEdit = usePermissions().hasAllPermissions(UPDATE_EXCEL_TEMPLATE);
  const isSubmitting =
    navigation.state === 'submitting' || navigation.state === 'loading' || closingAfterSave;
  const previewUrl = props.excelTemplate.templateFile?.url ?? null;
  const storedFileName = props.excelTemplate.templateFile?.name ?? null;

  return (
    <MountableSlideout
      onClose={onClose}
      title={canEdit ? 'Edit Excel template' : 'View Excel template'}
      size="7xl"
    >
      {!canEdit && (
        <PermissionNotice className="mb-6">
          You can view this Excel template but not change it.
        </PermissionNotice>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-8 min-w-0 w-full items-stretch">
        <Form
          onSubmit={handleSubmit}
          className="col-span-1 sm:col-span-2 min-w-0 flex min-h-0 flex-col sm:min-h-[calc(100vh-6rem)]"
          noValidate
        >
          <Fieldset
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain"
            disabled={isSubmitting || !canEdit}
          >
            <FieldGroup className="min-w-0">
              {props.excelTemplate.error && (
                <ErrorMessage>
                  Processing failed: {props.excelTemplate.error}. Fix the workbook or its guidance
                  and save to try again.
                </ErrorMessage>
              )}

              <Field>
                <Label>Title</Label>
                <Description>
                  A short name shown in your library and in workspace tools.
                </Description>
                <Input
                  className="max-w-xs"
                  value={form.title}
                  onChange={(e) => {
                    const title = e.currentTarget.value;
                    updateForm(['title'], (prev) => ({ ...prev, title }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['title'], { exact: true })}
                />
                {!!getValidationError(validationErrors, ['title'], { exact: true }) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['title'], { exact: true })}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Description</Label>
                <Description>What this Excel template is for (shown in the library).</Description>
                <Textarea
                  value={form.description}
                  rows={2}
                  resizable={false}
                  onChange={(e) => {
                    const description = e.currentTarget.value;
                    updateForm(['description'], (prev) => ({ ...prev, description }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['description'])}
                />
                {!!getValidationError(validationErrors, ['description']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['description'])}
                  </ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Setup guidance (optional)</Label>
                <Description>
                  Extra instructions for Cara that apply to the whole workbook. Use for context that
                  is not obvious from the file alone (naming conventions, cells to preserve).
                </Description>
                <AutoResizeTextarea
                  value={form.fillGuidance ?? ''}
                  rows={4}
                  resizable={false}
                  onChange={(e) => {
                    const fillGuidance = e.currentTarget.value;
                    updateForm(['fillGuidance'], (prev) => ({ ...prev, fillGuidance }));
                  }}
                  placeholder="Example: Never overwrite the totals row; it is calculated by formulas."
                />
              </Field>

              <Field>
                <Label>Workbook (.xlsx)</Label>
                <Description>
                  {storedFileName
                    ? `Currently ${storedFileName}. Uploading a new file replaces it and re-processes every tab.`
                    : 'Upload the workbook Cara should fill.'}
                </Description>
                <FileInput
                  invalid={!!getValidationError(validationErrors, ['uploadTemplate'])}
                  onChange={(change) => setReplacementWorkbook(change.files[0]?.upload ?? null)}
                />
                {!!getValidationError(validationErrors, ['uploadTemplate']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['uploadTemplate'])}
                  </ErrorMessage>
                )}
              </Field>

              <ExcelTemplateTabFields
                form={form}
                updateForm={updateForm}
                getValidationError={(parts) => getValidationError(validationErrors, parts)}
              />

              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </FieldGroup>
          </Fieldset>
          <div className="mt-auto flex flex-shrink-0 flex-col items-center gap-3 py-8 *:w-full sm:flex-row sm:*:w-auto">
            <Button type="button" color="white" disabled={isSubmitting} onClick={onClose}>
              Close
            </Button>
            {!canEdit ? null : visibilityEditAllowed ? (
              <VisibilitySplitButton
                type="submit"
                value={visibility}
                onChange={setVisibility}
                loading={isSubmitting}
              >
                Update Excel template
              </VisibilitySplitButton>
            ) : (
              <Button type="submit" color="sky" loading={isSubmitting}>
                Update Excel template
              </Button>
            )}
          </div>
        </Form>
        <div className="col-span-1 sm:col-span-3 min-w-0">
          {previewUrl && (
            <div className="sticky top-0 flex flex-col h-[calc(100vh-6rem)]">
              <div className="flex-1 min-h-0">
                <OfficeOnlineFilePreview
                  downloadUrl={previewUrl}
                  iframeTitle="Excel workbook preview"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </MountableSlideout>
  );
}
